import React, { FC, useEffect, useReducer, useState, useCallback, useRef } from 'react';
import { AuthContext } from '.';
import { useNavigate } from 'react-router-dom';
import {
  LOGOUT,
  CLEAR_ERRORS,
  USER_LOADED,
  AUTH_ERROR,
  SET_LOADING,
  UPDATE_USER,
  SET_USER_LOCATION,
  LOGIN_SUCCESS,
  LOGIN_FAIL, LOGIN_SUCCESS_TOKEN, SET_SKY_TOKEN, LOGIN_FAIL_PIN,
} from '../types';

import { fetch as fetchRedirect, saveContractIds } from '../../utils/refetch';
import useLoginMutation from 'src/hooks/mutation/login/useLoginMutation';
import { INITIAL_STATE } from 'src/core/constants/constants';
import { headers } from 'src/hooks/constants';
import { useUserQuery } from 'src/hooks/query';
import { AuthReducer } from '.';
import { Container } from 'react-bootstrap';
import { CustomSpinner } from 'src/shared/Spinner';
import { createUrl } from 'src/core/api/utils/createUrl';
import PusherSingleton from '../../utils/pusherSingleton';

type Props = {
  children: React.ReactNode;
};

const AuthState: FC<Props> = ({ children }) => {
  const { mutate, isLoading } = useLoginMutation();
  const [state, dispatch] = useReducer(AuthReducer, INITIAL_STATE);
  const navigate = useNavigate();
  const { user, isError, isUserLoading, isSuccess, refetchUserQuery } = useUserQuery(!!state.token);
  const [initialized, setInitialized] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const isLoggingInRef = useRef<boolean>(false);

  const closeSessionModal = (): void => {
    setShowModal(false);
  };

  const loadUser = useCallback(async () => {
    refetchUserQuery();
  }, [refetchUserQuery]);

  const logout = useCallback(() => {
    const token = localStorage.getItem('blue_token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // logout
    fetchRedirect(createUrl(`/logout`))
      .then(({ body }: { body: any }) => {
        localStorage.removeItem('blue_webusername');
        localStorage.removeItem('blue_token');
        localStorage.clear();
        dispatch({
          type: LOGOUT,
          payload: { error: '' },
        });
        navigate('/de/logout');
      })
      .catch(() => {
        localStorage.removeItem('blue_webusername');
        localStorage.removeItem('blue_token');
        localStorage.clear();
        dispatch({
          type: LOGOUT,
          payload: { error: '' },
        });
        navigate('/de/logout');
      });
  }, [navigate]);

  useEffect(() => {
    if (!isUserLoading && localStorage.getItem('blue_token')) {
      dispatch({
        type: SET_LOADING,
      });
    }
    if(!localStorage.getItem('blue_token')){
      setInitialized(true);
    }
  }, [isUserLoading]);

  // Handle user loading success/error states
  useEffect(() => {
    if (isSuccess && (state.isAuthenticated || state.token)) {
      setInitialized(true);
      dispatch({
        type: USER_LOADED,
        payload: { user: user },
      });

      if (user?.user_id) {
        try {
          const pusher = PusherSingleton.getInstance();

          // Unsubscribe from any existing session first to prevent duplicate handlers
          try {
            pusher.unsubscribe('session_end');
          } catch (error) {
            // Channel might not exist yet, ignore
          }

          const sessionChannel = pusher.subscribe('session_end');

          const eventHandler = function(data: any) {
            // Don't logout if currently in login process
            if (data?.success && !isLoggingInRef.current) {
              try {
                pusher.unsubscribe('session_end');
                logout();
              } catch (error) {
                console.warn('Error during session end cleanup:', error);
                logout();
              }
            } else if (data?.success && isLoggingInRef.current) {
              console.log('[Pusher] Ignoring session_end during login process');
            }
          };

          sessionChannel.bind(`session_${user.user_id}`, eventHandler);

          // Cleanup function to unsubscribe when component unmounts or user changes
          return () => {
            try {
              sessionChannel.unbind(`session_${user.user_id}`, eventHandler);
              pusher.unsubscribe('session_end');
            } catch (error) {
              console.warn('Error cleaning up Pusher subscription:', error);
            }
          };
        } catch (error) {
          console.warn('Failed to setup session management:', error);
        }
      }
    }

    if (isError) {
      setInitialized(true);
      if (!localStorage.getItem('blue_token')) {
        dispatch({
          type: LOGOUT,
          payload: { error: '' },
        });
        navigate('/de/logout');
      } else {
        dispatch({
          type: AUTH_ERROR,
          payload: { error: 'Something went wrong with User' },
        });
      }
    }
  }, [isError, isSuccess, user, logout, navigate]);

  useEffect(() => {
    const handleTokenExpired = (event: CustomEvent) => {
      console.warn('Token expired during API call, logging out user:', event.detail);
      logout();
    };

    window.addEventListener('token-expired', handleTokenExpired as EventListener);
    
    return () => {
      window.removeEventListener('token-expired', handleTokenExpired as EventListener);
    };
  }, [logout]);

  if (isUserLoading && (!!state.token || state.isAuthenticated)) {
    return (
      <Container>
        <CustomSpinner />
      </Container>
    );
  }

  const setOverrideData = (data:any) => {
    if (data.data.contract_ids) {
      saveContractIds(data.data.contract_ids);
    }
    if (data.data.country) {
      localStorage.setItem('country_id', data.data.country);
    }
    if (data.data.username) {
      localStorage.setItem('username', data.data.username);
    }
    localStorage.setItem('sky_token', data.data.sky_token);
    localStorage.setItem('sky_refresh_token', data.data.sky_refresh_token);
    dispatch({
      type: SET_SKY_TOKEN,
      payload: {},
    });
  };

  // Clear Errors
  const clearErrors = () => dispatch({ type: CLEAR_ERRORS });

  const updateUser = (data: { balance: number }) => {
    dispatch({
      type: UPDATE_USER,
      payload: data,
    });
  };
  const setUserLocation = (data: { userLocation: string }) => {
    dispatch({
      type: SET_USER_LOCATION,
      payload: data,
    });
  };

  const loginWithToken = (data: { token: string }) => {
    dispatch({
      type: LOGIN_SUCCESS_TOKEN,
      payload: data,
    });
  };

  const login = (loginData: {
    code?: string,
    state?: string,
    redirect_uri?: string,
    pin?: string,
    deleteSession?: boolean
  }) => {

    // Set flag to prevent logout during login process
    isLoggingInRef.current = true;
    dispatch({
      type: SET_LOADING,
    });
    mutate(
      { ...loginData },
      {
        onSuccess: async (data: any) => {
          if (data.status === 'success') {
            if (data.data.contract_ids) {
              saveContractIds(data.data.contract_ids);
            }
            if (data.data.country) {
              localStorage.setItem('country_id', data.data.country);
            }
            if (data.data.username) {
              localStorage.setItem('username', data.data.username);
            }
            if (loginData.code) {
              localStorage.setItem('sky_token', data.data.sky_token);
              localStorage.setItem('sky_refresh_token', data.data.sky_refresh_token);
              dispatch({
                type: SET_SKY_TOKEN,
                payload: {},
              });
              isLoggingInRef.current = false;
              return;
            }

            // Pusher session management is handled in the user loading useEffect (lines 88-153)
            // This prevents duplicate subscriptions and ensures proper cleanup

            try {
              localStorage.setItem('blue_webusername', data.username);
            } catch (error) {
              console.warn('Failed to save username to localStorage:', error);
            }
            if (loginData.pin) {
              console.log('SUCCES REDIRECTION');
              try {
                navigate(localStorage.getItem('login_redirection') || '/');
              } catch (error) {
                console.warn('Failed to get redirection from localStorage:', error);
                navigate('/');
              }
            }
            dispatch({
              type: LOGIN_SUCCESS,
              payload: data.data,
            });

            // Reset login flag after delay to allow session_end events to pass
            setTimeout(() => {
              isLoggingInRef.current = false;
            }, 5000);
          }
          if (data.status === 403 && data?.body?.data?.returnCode === 'session_management') {
            setShowModal(true);
            isLoggingInRef.current = false;
          }
        },
        onError(error: any, variables, context) {
          // Reset login flag on error
          isLoggingInRef.current = false;

          if (error.status === 403 && error?.body.data?.returnCode === 'session_management') {
            setShowModal(true);
          }
          if (loginData.pin) {
            dispatch({
              type: LOGIN_FAIL_PIN,
              payload: { error: error?.body.message },
            });
            return;
          }
          dispatch({
            type: LOGIN_FAIL,
            payload: { error: error?.body.message },
          });
        },
      },
    );
  };

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        showModal: showModal,
        loginWithToken: loginWithToken,
        confirmPinRequired: state.confirmPinRequired,
        closeSessionModal,
        loading: !initialized || state.loading || isLoading,
        user: state.user,
        error: state.error,
        euPortabilityStatusPopup: state.euPortabilityStatusPopup,
        euPortabilityStatusPopupText: state.euPortabilityStatusPopupText,
        firstLogin: state.firstLogin,
        userLocation: state.userLocation,
        login,
        logout,
        clearErrors,
        loadUser,
        updateUser,
        setUserLocation,
        setOverrideData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthState;
